import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import NotificationsOffRoundedIcon from '@mui/icons-material/NotificationsOffRounded';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    ListItemIcon,
    ListItemText,
    MenuItem,
    Snackbar,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useI18n } from '../../i18n';
import { isNative } from '../../monetization/config';
import { RootState } from '../../store';
import { playCue } from '../feedback';
import { remindersSupported, requestReminderPermission } from '../reminders';
import { dismissReminderOffer, setReminders } from '../tarotSlice';

/** Asks for notification permission, and only turns reminders on once it is granted. */
export const useReminderToggle = () => {
    const { t } = useI18n();
    const dispatch = useDispatch();
    const on = useSelector((s: RootState) => s.tarot.reminders);
    const [message, setMessage] = useState('');

    const toggle = async () => {
        playCue('ui');
        if (on) {
            dispatch(setReminders(false));
            return;
        }
        if (!remindersSupported()) {
            setMessage(t('reminders.unsupported'));
            return;
        }
        if (await requestReminderPermission()) {
            dispatch(setReminders(true));
            setMessage(t(isNative() ? 'reminders.on' : 'reminders.webNote'));
        } else setMessage(t('reminders.denied'));
    };

    const toast = (
        <Snackbar
            open={Boolean(message)}
            autoHideDuration={6000}
            onClose={() => setMessage('')}
            message={message}
        />
    );
    return { on, toggle, toast };
};

/** Offers reminders once, on the first visit. Permission needs a tap, so this asks for one. */
export const ReminderOffer: React.FC = () => {
    const { t } = useI18n();
    const dispatch = useDispatch();
    const asked = useSelector((s: RootState) => s.tarot.remindersAsked);
    const { on, toggle, toast } = useReminderToggle();
    const [ready, setReady] = useState(false);

    useEffect(() => {
        const id = window.setTimeout(() => setReady(true), 1500);
        return () => window.clearTimeout(id);
    }, []);

    const blocked =
        !isNative() &&
        typeof Notification !== 'undefined' &&
        Notification.permission === 'denied';
    const open = ready && !asked && !on && !blocked && remindersSupported();
    return (
        <>
            <Dialog
                open={open}
                onClose={() => dispatch(dismissReminderOffer())}
            >
                <DialogTitle>{t('reminders.offerTitle')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('reminders.offerBody')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => dispatch(dismissReminderOffer())}>
                        {t('reminders.notNow')}
                    </Button>
                    <Button variant="contained" onClick={toggle}>
                        {t('reminders.offer')}
                    </Button>
                </DialogActions>
            </Dialog>
            {toast}
        </>
    );
};

export const ReminderMenuItem: React.FC<{
    on: boolean;
    onToggle: () => void;
}> = ({ on, onToggle }) => {
    const { t } = useI18n();
    return (
        <MenuItem
            onClick={onToggle}
            selected={on}
            data-testid="reminders-toggle"
        >
            <ListItemIcon>
                {on ? (
                    <NotificationsActiveRoundedIcon />
                ) : (
                    <NotificationsOffRoundedIcon />
                )}
            </ListItemIcon>
            <ListItemText>{t('reminders.title')}</ListItemText>
        </MenuItem>
    );
};
